// Phase NAATI-MAX B2 — /translate/naati/interpreting
import { createFileRoute } from "@tanstack/react-router";
import NaatiKnowledgePage from "@/components/NaatiKnowledgePage";
import { NAATI_INTERPRETING_SERVICES } from "@/data/naati-interpreting";
import { canonicalAndHreflang, ctaDesc, ctaTitle } from "@/lib/seo";

const SITE = "https://nyclanguageinstitute.com";
const URL = `${SITE}/translate/naati/interpreting`;

const faqs = [
  { q: "ล่าม NAATI ในโรงพยาบาลรัฐออสเตรเลีย ต้องระดับไหน?", a: "ระดับ CSHI (Certified Specialist Health Interpreter) ขึ้นไป — โรงพยาบาลรัฐและ Mental Health Tribunal ปฏิเสธล่ามที่ต่ำกว่านี้." },
  { q: "ล่ามศาลออสเตรเลีย ต้องระดับไหน?", a: "ระดับ CSLI (Certified Specialist Legal Interpreter) ขึ้นไปทุกศาล — Magistrates, District, Federal, Family, Federal Circuit และ AAT ผู้พิพากษาจะสั่งเลื่อนคดีทันทีหากล่ามที่จองไว้ไม่มีใบ CSLI ปัจจุบัน เพราะเป็นเงื่อนไขบังคับตาม National Interpreting Standard" },
  { q: "ค่าจ้างล่ามนับเป็นชั่วโมงหรือเป็นงาน?", a: "ส่วนใหญ่นับเป็นชั่วโมง (minimum 2 ชั่วโมง) บวกค่าเดินทาง หากจองนอกเขต Sydney/Melbourne/Brisbane ต้องบวกค่าเดินทางและที่พักแยก." },
  { q: "จองล่ามล่วงหน้านานแค่ไหน?", a: "ล่าม CSHI/CSLI ควรจองล่วงหน้าอย่างน้อย 2-4 สัปดาห์ ล่าม CCI ควรจองล่วงหน้า 6-8 สัปดาห์ เพราะมีจำนวนจำกัด." },
];

export const Route = createFileRoute("/translate/naati/interpreting")({
  head: () => ({
    meta: [
      { title: ctaTitle("บริการล่ามรับรอง NAATI ไทย-อังกฤษ ออสเตรเลีย | NYC", "/translate/naati/interpreting", "th")},
      { name: "description", content: ctaDesc("ล่ามรับรอง NAATI ไทย-อังกฤษ ทั่วออสเตรเลีย — ศาล (CSLI) การแพทย์ (CSHI) ธุรกิจ (CI) ประชุม (CCI) แจ้งวันและสถานที่ ทักไลน์เช็กคิวล่ามว่างได้ทันที", "/translate/naati/interpreting", "th")},
      { property: "og:title", content: ctaTitle("บริการล่ามรับรอง NAATI ไทย-อังกฤษ ออสเตรเลีย | NYC", "/translate/naati/interpreting", "th")},
      { property: "og:description", content: ctaDesc("ล่ามรับรอง NAATI ไทย-อังกฤษ ทั่วออสเตรเลีย — ศาล การแพทย์ ธุรกิจ ประชุม ทักไลน์เช็กคิวล่ามว่างในวันที่คุณต้องการ", "/translate/naati/interpreting", "th")},
      { property: "og:url", content: URL },
    ],
    links: canonicalAndHreflang("/translate/naati/interpreting", "/en/translate/naati/interpreting"),
    scripts: [
      { type: "application/ld+json", children: JSON.stringify({
        "@context": "https://schema.org", "@type": "Service",
        "@id": `${URL}#service`, name: "NAATI Certified Interpreting (Thai-English)",
        inLanguage: "th", serviceType: "Certified Interpreting",
        areaServed: { "@type": "Country", name: "Australia" },
        provider: { "@type": "ProfessionalService", name: "NYC Language Institute", url: SITE },
        offers: {
          "@type": "AggregateOffer",
          priceCurrency: "THB",
          lowPrice: Math.min(...NAATI_INTERPRETING_SERVICES.map((s) => s.hourlyFromThb)),
          highPrice: Math.max(...NAATI_INTERPRETING_SERVICES.map((s) => s.hourlyFromThb)),
          offerCount: NAATI_INTERPRETING_SERVICES.length,
          availability: "https://schema.org/InStock",
        },
      }) },
      { type: "application/ld+json", children: JSON.stringify({
        "@context": "https://schema.org", "@type": "FAQPage", inLanguage: "th",
        mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
      }) },
      { type: "application/ld+json", children: JSON.stringify({
        "@context": "https://schema.org", "@type": "WebPage", "@id": `${URL}#speakable`, url: URL,
        speakable: { "@type": "SpeakableSpecification", cssSelector: ["[data-speakable]", "h1", ".direct-answer"] },
      }) },
    ],
  }),
  component: () => (
    <NaatiKnowledgePage
      locale="th"
      eyebrow="NAATI Interpreting Services"
      h1="บริการล่ามรับรอง NAATI ไทย-อังกฤษ ทั่วออสเตรเลีย"
      lead="ล่าม NAATI ใช้ทั้งในศาล โรงพยาบาลรัฐ ที่ทำการตำรวจ Centrelink สำนักงานทนาย และการประชุมระดับชาติ — เรามีล่ามขึ้นทะเบียนครบทุกระดับตั้งแต่ CI จนถึง CCI พร้อมจองในเมืองหลัก."
      speakableAnswer="ล่าม NAATI ในศาลออสเตรเลียต้องเป็นระดับ CSLI ขึ้นไป. ในโรงพยาบาลรัฐต้องเป็น CSHI. การประชุมระดับชาติต้องเป็น CCI. ค่าจ้างเริ่มต้น ฿2,500/ชั่วโมง minimum 2 ชั่วโมง."
      sections={[
        {
          heading: "4 หมวดบริการล่าม NAATI",
          table: {
            headers: ["บริการ", "ระดับที่ใช้", "เริ่มต้น (THB/ชม.)", "Minimum"],
            rows: NAATI_INTERPRETING_SERVICES.map((s) => [
              `${s.nameTh}\n${s.domainTh}`,
              s.requiredLevel,
              `฿${s.hourlyFromThb.toLocaleString()}`,
              `${s.minHours} ชั่วโมง`,
            ]),
          },
        },
        ...NAATI_INTERPRETING_SERVICES.map((s) => ({
          heading: s.nameTh,
          paragraph: `ระดับที่ต้องใช้: NAATI ${s.requiredLevel}. เริ่มต้น ฿${s.hourlyFromThb.toLocaleString()}/ชั่วโมง (minimum ${s.minHours} ชั่วโมง).`,
          bullets: s.useCasesTh,
        })),
        {
          heading: "ขั้นตอนการจองล่าม NAATI",
          bullets: [
            "ส่งรายละเอียดงาน — ประเภท, สถานที่, วันเวลา, ระยะเวลา",
            "เราจัด match ล่ามตามระดับและประสบการณ์ภายใน 24 ชั่วโมง",
            "ยืนยัน + วางมัดจำ 50% เพื่อ lock ล่าม",
            "วันงาน — ล่ามรายงานตัว 15 นาทีก่อนเวลา + ส่ง report สรุปหลังจบ",
          ],
        },
      ]}
      faqs={faqs}
      cta={{ label: "จองล่าม NAATI", href: "/translate/naati" }}
    />
  ),
});
